import React, { useContext, useMemo, useCallback, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, matchRoutes } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { useDeepCompareEffect } from '@fuse/hooks';
import FuseLayouts from '@fuse/layouts/FuseLayouts';
import _ from 'lodash';
import * as Velocity from 'velocity-animate';
import { defaults as Chartjs2Defaults } from 'react-chartjs-2';
import AppContext from 'app/AppContext';
import * as Actions from 'app/store/actions';
import { generateSettings } from 'app/store/reducers/fuse/settings.reducer';

// Define styles using makeStyles hook
const useStyles = makeStyles(theme => ({
  '@global': {
    'code:not([class*="language-"])': {
      color: theme.palette.secondary.dark,
      backgroundColor:
        theme.palette.type === 'light'
          ? 'rgba(255, 255, 255, .9)'
          : 'rgba(0, 0, 0, .9)',
      padding: '2px 3px',
      borderRadius: 2,
      lineHeight: 1.7
    },
    'table.simple tbody tr td': {
      borderColor: theme.palette.divider
    },
    'table.simple thead tr th': {
      borderColor: theme.palette.divider
    },
    'a:not([role=button])': {
      color: theme.palette.secondary.main,
      textDecoration: 'none',
      '&:hover': {
        textDecoration: 'underline'
      }
    },
    '[class^="border-"]': {
      borderColor: theme.palette.divider
    },
    '[class*="border-"]': {
      borderColor: theme.palette.divider
    },
    hr: {
      borderColor: theme.palette.divider
    }
  },
  root: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary
  }
}));

// Function to handle rendering logic based on route matching and settings
const shouldAwaitRender = (
  matched,
  defaultSettings,
  newSettings,
  setNewSettings
) => {
  let _newSettings;

  // Check if the matched route has specific settings
  if (matched && matched.route.settings) {
    const routeSettings = matched.route.settings;

    // add defaultSettings => FuseLayoutConfigs => Layout1
    _newSettings = generateSettings(defaultSettings, routeSettings);
  } else if (!_.isEqual(newSettings.current, defaultSettings)) {
    // Reset to default settings if no specific settings are found
    _newSettings = _.merge({}, defaultSettings);
  } else {
    _newSettings = newSettings.current;
  }

  // Update newSettings if there are changes
  if (!_.isEqual(newSettings.current, _newSettings)) {
    setNewSettings(_newSettings);
  }

  // Toggle animations based on the new settings
  AnimationToggle(_newSettings);
};

// Function to toggle animations based on settings
const AnimationToggle = _settings => {
  if (!_settings.animations) {
    document.body.classList.add('no-animate');
    Velocity.mock = true;
    Chartjs2Defaults.global.animation.duration = 0;
    Chartjs2Defaults.global.hover.animationDuration = 0;
  } else {
    document.body.classList.remove('no-animate');
    Velocity.mock = false;
    Chartjs2Defaults.global.animation.duration = 1000;
    Chartjs2Defaults.global.hover.animationDuration = 400;
  }
};

// Main component
const FuseLayout = props => {
  const dispatch = useDispatch();
  const settings = useSelector(({ fuse }) => fuse.settings.current);
  const defaultSettings = useSelector(({ fuse }) => fuse.settings.defaults);
  const appContext = useContext(AppContext);
  const { routes } = appContext;
  const classes = useStyles(props);
  const { pathname } = useLocation(); // Destructure pathname from location
  const matched = matchRoutes(routes, pathname)?.[0]; // Find matched route
  const newSettings = useRef(null); // Use ref to store new settings

  // Callback to update newSettings
  const setNewSettings = useCallback(_newSettings => {
    newSettings.current = _newSettings;
  }, []);

  // Memoize shouldAwaitRender function to avoid unnecessary re-renders
  useMemo(() => {
    shouldAwaitRender(matched, defaultSettings, newSettings, setNewSettings);
  }, [matched, defaultSettings, newSettings, setNewSettings]);

  // Use deep comparison effect to update settings if they change
  useDeepCompareEffect(() => {
    if (!_.isEqual(newSettings.current, settings)) {
      dispatch(Actions.setSettings(newSettings.current));
    }
  }, [dispatch, newSettings.current, settings]);

  // Memoize the layout component based on the current settings
  const Layout = useMemo(
    () => FuseLayouts[settings.layout.style],
    [settings.layout.style]
  );

  // Render the layout if settings are in sync
  return _.isEqual(newSettings.current, settings) ? (
    <Layout classes={{ root: classes.root }} {...props} />
  ) : null;
};

export default React.memo(FuseLayout);
